import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useBusinessLine } from '../contexts/BusinessLineContext';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  BarChart3,
  TrendingUp,
  Download,
  Calendar,
  Target,
  Users,
  DollarSign,
  Receipt,
  FileText,
  Building,
  Shield,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#1a365d', '#2d8659', '#6b7280', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const SOURCE_COLOR_MAP = {
  'Facebook': '#1877f2',
  'Facebook Messenger': '#0099ff',
  'Instagram': '#e91e8c',
  'Instagram DMs': '#f8a5c2',
  'Llamada en Frío': '#d97706',
  'Manual': '#6b7280',
  'Referido': '#7c3aed',
  'Reporte de Ventas': '#eab308',
  'WhatsApp': '#25d366',
  'website': '#f97316',
};

const getSourceColor = (sourceName) =>
  SOURCE_COLOR_MAP[sourceName] || '#84cc16';

const getStatusLabel = (status) => {
  const map = {
    'active': { label: 'Activa', color: 'bg-green-500' },
    'pending_payment': { label: 'Pendiente de Pago', color: 'bg-yellow-500' },
    'pending': { label: 'Pendiente', color: 'bg-yellow-500' },
    'cancelled': { label: 'Cancelada', color: 'bg-red-500' },
    'expired': { label: 'Vencida', color: 'bg-red-500' },
  };
  return map[status] || { label: status, color: 'bg-gray-500' };
};

const normalizeSource = (source) => {
  const map = {
    'manual': 'Manual',
    'reporte_ventas': 'Reporte de Ventas',
    'instagram': 'Instagram',
    'facebook': 'Facebook',
    'whatsapp': 'WhatsApp',
    'llamada_fria': 'Llamada en Frío',
    'llamada en frío': 'Llamada en Frío',
    'referido': 'Referido',
    'facebook messenger': 'Facebook Messenger',
    'instagram dms': 'Instagram DMs',
  };
  return map[source?.toLowerCase()] || source;
};

const normalizeStatus = (status) => {
  const map = {
    'nuevo': 'Nuevo',
    'contactado': 'Contactado',
    'en_negociacion': 'En Negociación',
    'pendiente_de_pago': 'Pendiente de Pago',
    'ganado': 'Ganado',
    'perdido': 'Perdido',
  };
  return map[status] || status;
};

const reportTypes = [
  { id: 'leads', label: 'Leads', icon: Target },
  { id: 'clients', label: 'Clientes', icon: Users },
  { id: 'agents', label: 'Agentes', icon: Users },
  { id: 'policies', label: 'Pólizas', icon: Shield },
  { id: 'inmuebles', label: 'Inmuebles', icon: Building },
];

const LEADS_PAGE_SIZE = 20;
const CLIENTS_PAGE_SIZE = 20;
const POLICIES_PAGE_SIZE = 20;

export const ReportsPage = () => {
  const { selectedLine, isAllLines, changeBusinessLine } = useBusinessLine();
  const [reportType, setReportType] = useState('leads');
  const [leadsBySource, setLeadsBySource] = useState([]);
  const [leadsByStatus, setLeadsByStatus] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [financialStats, setFinancialStats] = useState(null);
  const [collectionsReport, setCollectionsReport] = useState([]);
  const [policiesData, setPoliciesData] = useState([]);
  const [polizasDistrib, setPolizasDistrib] = useState([]);
  const [productoFiltro, setProductoFiltro] = useState('');
  const [inmueblesData, setInmueblesData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [leadsData, setLeadsData] = useState([]);
  const [agentsData, setAgentsData] = useState([]);
  const [agentLeadsTotal, setAgentLeadsTotal] = useState(0);
  const [agentClientsTotal, setAgentClientsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [chartKey, setChartKey] = useState(0);
  // Paginación leads
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsTotalPages, setLeadsTotalPages] = useState(1);
  const [leadsTotalRecords, setLeadsTotalRecords] = useState(0);
  // Paginación clientes
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsTotalPages, setClientsTotalPages] = useState(1);
  const [clientsTotalRecords, setClientsTotalRecords] = useState(0);
  const [clientsActiveTotal, setClientsActiveTotal] = useState(0);
  // Paginación y filtros pólizas
  const [policiesPage, setPoliciesPage] = useState(1);
  const [policiesTotalPages, setPoliciesTotalPages] = useState(1);
  const [policiesTotalRecords, setPoliciesTotalRecords] = useState(0);
  const [policiesDateFilter, setPoliciesDateFilter] = useState('created_month');
  const [policiesStartDate, setPoliciesStartDate] = useState('');
  const [policiesActiveCount, setPoliciesActiveCount] = useState(0);
  const [policiesPendingCount, setPoliciesPendingCount] = useState(0);

  const getPoliciesDateParams = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    if (policiesDateFilter === 'created_month') {
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      return { created_from: `${year}-${month}-01`, created_to: `${year}-${month}-${String(lastDay).padStart(2, '0')}` };
    }
    if (policiesDateFilter === 'created_year') {
      return { created_from: `${year}-01-01`, created_to: `${year}-12-31` };
    }
    if (policiesDateFilter === 'start_date' && policiesStartDate) {
      return { start_date_from: policiesStartDate };
    }
    return {};
  };

  useEffect(() => {
    setChartKey(k => k + 1);
    if (reportType === 'leads') {
      setLeadsPage(1);
      fetchLeadsReport(1);
    } else if (reportType === 'clients') {
      setClientsPage(1);
      fetchClientsReport(1);
    } else if (reportType === 'policies') {
      setPoliciesPage(1);
      fetchPoliciesReport(1);
    } else {
      fetchOtherReport();
    }
  }, [reportType, period, selectedLine]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reportType === 'policies') {
      fetchPoliciesReport(policiesPage);
    }
  }, [policiesPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reportType === 'policies') {
      setPoliciesPage(1);
      fetchPoliciesReport(1);
    }
  }, [policiesDateFilter, policiesStartDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeadsReport = async (page) => {
    setLoading(true);
    try {
      const leadsParams = new URLSearchParams({ page, page_size: LEADS_PAGE_SIZE });
      if (!isAllLines) leadsParams.append('business_line', selectedLine);
      const chartParams = isAllLines ? '' : `?business_line=${selectedLine}`;
      const [sourceRes, statusRes, leadsRes] = await Promise.all([
        axios.get(`${API}/reports/leads-by-source${chartParams}`),
        axios.get(`${API}/reports/leads-by-status${chartParams}`),
        axios.get(`${API}/leads?${leadsParams}`)
      ]);
      setLeadsBySource(sourceRes.data);
      setLeadsByStatus(statusRes.data);
      setLeadsData(leadsRes.data.items || []);
      setLeadsTotalPages(leadsRes.data.total_pages || 1);
      setLeadsTotalRecords(leadsRes.data.total || 0);
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsReport = async (page) => {
    setLoading(true);
    try {
      const clientsParams = new URLSearchParams({ page, page_size: CLIENTS_PAGE_SIZE });
      if (!isAllLines) clientsParams.append('business_line', selectedLine);
      const activeParams = new URLSearchParams({ status: 'activo', page: 1, page_size: 1 });
      if (!isAllLines) activeParams.append('business_line', selectedLine);
      const [clientsRes, activeRes] = await Promise.all([
        axios.get(`${API}/clients?${clientsParams}`),
        axios.get(`${API}/clients?${activeParams}`)
      ]);
      setClientsData(clientsRes.data.items || []);
      setClientsTotalPages(clientsRes.data.total_pages || 1);
      setClientsTotalRecords(clientsRes.data.total || 0);
      setClientsActiveTotal(activeRes.data.total || 0);
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const fetchPoliciesReport = async (page) => {
    setLoading(true);
    try {
      const dateParams = getPoliciesDateParams();
      const params = new URLSearchParams({ page, page_size: POLICIES_PAGE_SIZE, ...dateParams });
      if (!isAllLines) params.append('business_line', selectedLine);
      const distribParams = new URLSearchParams();
      if (!isAllLines) distribParams.append('aseguradora', selectedLine);
      const [policiesRes, distribRes] = await Promise.all([
        axios.get(`${API}/policies?${params}`),
        axios.get(`${API}/policies/distribution?${distribParams}`),
      ]);
      setPoliciesData(policiesRes.data.items || []);
      setPoliciesTotalPages(policiesRes.data.total_pages || 1);
      setPoliciesTotalRecords(policiesRes.data.total || 0);
      setPoliciesActiveCount(policiesRes.data.active_count || 0);
      setPoliciesPendingCount(policiesRes.data.pending_count || 0);
      setPolizasDistrib(distribRes.data.items || []);
    } catch (error) {
      console.error('Error al cargar pólizas:', error);
      toast.error('Error al cargar pólizas');
    } finally {
      setLoading(false);
    }
  };

  const fetchOtherReport = async () => {
    setLoading(true);
    try {
      if (reportType === 'agents') {
        const blQ = isAllLines ? '' : `?business_line=${selectedLine}`;
        const blA = isAllLines ? '' : `&business_line=${selectedLine}`;
        const [perfRes, agentsRes, leadsSourceRes, clientsCountRes] = await Promise.all([
          axios.get(`${API}/reports/agent-performance${blQ}`),
          axios.get(`${API}/agents${blQ}`),
          axios.get(`${API}/reports/leads-by-source${blQ}`),
          axios.get(`${API}/clients?page=1&page_size=1${blA}`)
        ]);
        setAgentPerformance(perfRes.data);
        setAgentsData(agentsRes.data || []);
        setAgentLeadsTotal((leadsSourceRes.data || []).reduce((sum, s) => sum + (s.count || 0), 0));
        setAgentClientsTotal(clientsCountRes.data?.total ?? 0);
      } else if (reportType === 'cobranza') {
        const [statsRes, collectionsRes] = await Promise.all([
          axios.get(`${API}/financial-stats`),
          axios.get(`${API}/reports/collections`)
        ]);
        setFinancialStats(statsRes.data);
        setCollectionsReport(collectionsRes.data);
      } else if (reportType === 'inmuebles') {
        setInmueblesData([
          { id: '1', address: 'Av. Reforma 123, CDMX', type: 'Departamento', status: 'En venta', price: 2500000, area: 120, created_at: '2026-01-15' },
          { id: '2', address: 'Calle Palmas 456, Guadalajara', type: 'Casa', status: 'Vendido', price: 4800000, area: 250, created_at: '2026-01-10' },
          { id: '3', address: 'Blvd. Costero 789, Cancún', type: 'Terreno', status: 'En venta', price: 1200000, area: 500, created_at: '2026-01-05' },
          { id: '4', address: 'Av. Universidad 321, Monterrey', type: 'Oficina', status: 'En renta', price: 35000, area: 80, created_at: '2025-12-20' },
        ]);
      }
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      let dataToExport = [];
      let fileName = `${reportType}_reporte.xlsx`;
      let headers = [];

      switch (reportType) {
        case 'leads':
          // Exportar datos de leads
          const leadsRes = await axios.get(`${API}/leads`);
          dataToExport = (leadsRes.data || []).map(lead => ({
            'Nombre': lead.name || '',
            'Email': lead.email || '',
            'Teléfono': lead.phone || '',
            'Fuente': lead.source || '',
            'Estado': lead.status || '',
            'Fecha de Contacto': lead.contact_date || '',
            'Notas': lead.notes || ''
          }));
          break;

        case 'clients':
          const clientsRes = await axios.get(`${API}/clients`);
          dataToExport = (clientsRes.data || []).map(client => ({
            'Nombre': client.name || '',
            'Email': client.email || '',
            'Teléfono': client.phone || '',
            'Estado': client.status || '',
            'Fecha de Nacimiento': client.birth_date || '',
            'Líneas de Negocio': (client.business_lines || []).join(', '),
            'Notas': client.notes || ''
          }));
          break;

        case 'agents':
          dataToExport = agentPerformance.map(a => ({
            'Nombre': a.agent?.full_name || '',
            'Email': a.agent?.email || '',
            'Total Leads': a.total_leads || 0,
            'Total Clientes': a.total_clients || 0,
            'Conversiones': a.conversions || 0,
            'Comisiones Totales': a.total_commissions || 0
          }));
          break;

        case 'policies':
          dataToExport = policiesData.map(policy => ({
            'Número de Póliza': policy.policy_number || '',
            'Cliente': policy.client_name || '',
            'Agente': policy.agent_name || '',
            'Línea de Negocio': policy.business_line || '',
            'Tipo de Producto': policy.product_type || '',
            'Prima': policy.premium || 0,
            'Fecha Inicio': policy.start_date || '',
            'Fecha Fin': policy.end_date || '',
            'Estado': policy.status === 'active' ? 'Activa' : policy.status === 'pending' ? 'Pendiente' : policy.status === 'expired' ? 'Vencida' : policy.status || ''
          }));
          break;

        case 'inmuebles':
          dataToExport = inmueblesData.map(inmueble => ({
            'Dirección': inmueble.address || '',
            'Tipo': inmueble.type || '',
            'Estado': inmueble.status || '',
            'Precio': inmueble.price || 0,
            'Área (m²)': inmueble.area || 0,
            'Fecha Registro': inmueble.created_at || ''
          }));
          break;

        case 'cobranza':
          dataToExport = collectionsReport.map(c => ({
            'Estado': c.status || '',
            'Total': c.total || 0,
            'Cantidad': c.count || 0
          }));
          break;

        default:
          toast.error('Tipo de reporte no soportado');
          return;
      }

      if (dataToExport.length === 0) {
        toast.error('No hay datos para exportar');
        return;
      }

      // Crear libro de Excel
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, reportType);

      // Ajustar ancho de columnas
      const columnWidths = Object.keys(dataToExport[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      worksheet['!cols'] = columnWidths;

      // Descargar archivo
      XLSX.writeFile(workbook, fileName);
      toast.success('Reporte exportado correctamente');
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('Error al exportar');
    }
  };

  // Calculate summary stats based on report type
  const getSummaryStats = () => {
    if (reportType === 'leads') {
      const totalLeads = leadsBySource.reduce((sum, s) => sum + s.count, 0);
      const wonLeads = leadsByStatus.find(s => s.status === 'ganado')?.count || 0;
      const conversionRate = totalLeads > 0 ? ((wonLeads / totalLeads) * 100).toFixed(1) : 0;
      return { totalLeads, wonLeads, conversionRate };
    }
    if (reportType === 'agents') {
      const totalAgents = agentPerformance.length;
      const totalLeads = agentLeadsTotal;
      const totalClientes = agentClientsTotal;
      const agentsConLead = agentPerformance.filter(a => a.total_leads > 0);
      const avgConversion = agentsConLead.length
        ? agentsConLead.reduce((sum, a) => sum + (a.conversion_rate || 0), 0) / agentsConLead.length
        : 0;
      return { totalAgents, totalLeads, totalClientes, avgConversion };
    }
    if (reportType === 'cobranza') {
      return financialStats || {};
    }
    if (reportType === 'policies') {
      const totalPolicies = policiesTotalRecords;
      const activePolicies = policiesActiveCount;
      const totalPremium = policiesData.reduce((sum, p) => sum + (p.premium || 0), 0);
      const expiredPolicies = policiesPendingCount;
      return { totalPolicies, activePolicies, totalPremium, expiredPolicies };
    }
    if (reportType === 'inmuebles') {
      const totalInmuebles = inmueblesData.length;
      const enVenta = inmueblesData.filter(i => i.status === 'En venta').length;
      const vendidos = inmueblesData.filter(i => i.status === 'Vendido').length;
      const valorTotal = inmueblesData.reduce((sum, i) => sum + (i.price || 0), 0);
      return { totalInmuebles, enVenta, vendidos, valorTotal };
    }
    if (reportType === 'clients') {
      return { totalClients: clientsTotalRecords, activeClients: clientsActiveTotal };
    }
    return {};
  };

  const stats = getSummaryStats();

  // Mock monthly data
  const monthlyTrend = [
    { month: 'Ene', value: 45000 },
    { month: 'Feb', value: 52000 },
    { month: 'Mar', value: 61000 },
    { month: 'Abr', value: 48000 },
    { month: 'May', value: 75000 },
    { month: 'Jun', value: 82000 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a365d]"></div>
      </div>
    );
  }

  const ReportIcon = reportTypes.find(r => r.id === reportType)?.icon || BarChart3;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reportes</h1>
          <p className="text-muted-foreground">Análisis y métricas del negocio</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedLine} onValueChange={changeBusinessLine}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las aseguradoras</SelectItem>
              <SelectItem value="allianz">Allianz</SelectItem>
              <SelectItem value="insignia">Insignia Life</SelectItem>
              <SelectItem value="qualitas">Quálitas</SelectItem>
              <SelectItem value="flipping">Bienes Raíces</SelectItem>
            </SelectContent>
          </Select>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="w-[200px]" data-testid="report-type-select">
              <ReportIcon className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Tipo de reporte" />
            </SelectTrigger>
            <SelectContent>
              {reportTypes.map(type => (
                <SelectItem key={type.id} value={type.id}>
                  <div className="flex items-center gap-2">
                    <type.icon className="h-4 w-4" />
                    {type.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]" data-testid="period-select">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Esta semana</SelectItem>
              <SelectItem value="month">Este mes</SelectItem>
              <SelectItem value="quarter">Este trimestre</SelectItem>
              <SelectItem value="year">Este año</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExport} data-testid="export-report-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar XLSX
          </Button>
        </div>
      </div>

      {/* LEADS REPORT */}
      {reportType === 'leads' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Leads</p>
                    <p className="text-3xl font-bold">{stats.totalLeads}</p>
                  </div>
                  <Target className="h-8 w-8 text-[#1a365d]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Conversiones</p>
                    <p className="text-3xl font-bold text-green-600">{stats.wonLeads}</p>
                  </div>
                  <Users className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Tasa de Conversión</p>
                    <p className="text-3xl font-bold">{stats.conversionRate}%</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-[#2d8659]" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Leads por Fuente</CardTitle>
                <CardDescription>Distribución de leads según el canal de origen</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%" key={`chart-${reportType}-${chartKey}-0`}>
                    <PieChart>
                      {(() => {
                        const consolidatedData = (() => {
                          const raw = (leadsBySource.length > 0 ? leadsBySource : [{ source: 'Sin datos', count: 1 }])
                            .filter(item => {
                              const label = normalizeSource(item.source || item._id);
                              return label && label.toLowerCase() !== 'n8n';
                            });
                          return raw.reduce((acc, item) => {
                            const label = normalizeSource(item.source);
                            const existing = acc.find(a => a.source === label);
                            if (existing) { existing.count += item.count; } else { acc.push({ source: label, count: item.count }); }
                            return acc;
                          }, []);
                        })();
                        return (
                          <Pie
                            data={consolidatedData}
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="count"
                            nameKey="source"
                          >
                            {consolidatedData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={getSourceColor(entry.source)} />
                            ))}
                          </Pie>
                        );
                      })()}
                      <Tooltip />
                      <Legend formatter={(value, entry) => `${value} (${entry.payload.count})`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pipeline de Ventas</CardTitle>
                <CardDescription>Estado actual de los leads</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%" key={`chart-${reportType}-${chartKey}-1`}>
                    <BarChart data={leadsByStatus} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" />
                      <YAxis
                        dataKey="status"
                        type="category"
                        width={100}
                        tick={{ fontSize: 12 }}
                        tickFormatter={normalizeStatus}
                      />
                      <Tooltip labelFormatter={normalizeStatus} />
                      <Bar 
                        dataKey="count" 
                        fill="#1a365d" 
                        radius={[0, 4, 4, 0]}
                        name="Cantidad"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Detalle de Leads</CardTitle>
              <CardDescription>Listado completo de leads registrados</CardDescription>
            </CardHeader>
            <CardContent>
              {leadsData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No hay datos</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Fuente</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Agente</TableHead>
                        <TableHead>Fecha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leadsData.map(lead => (
                        <TableRow key={lead.id}>
                          <TableCell className="font-medium">{lead.name || '-'}</TableCell>
                          <TableCell>{lead.source || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{normalizeStatus(lead.status) || '-'}</Badge>
                          </TableCell>
                          <TableCell>{lead.agent_name || '-'}</TableCell>
                          <TableCell className="text-sm">{lead.created_at ? formatDate(lead.created_at) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {leadsTotalPages > 1 && (
                <div className="flex items-center justify-center gap-4 py-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { const p = leadsPage - 1; setLeadsPage(p); fetchLeadsReport(p); }}
                    disabled={leadsPage === 1}
                    className="gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    Página {leadsPage} de {leadsTotalPages} &nbsp;·&nbsp; Total: {leadsTotalRecords} leads
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { const p = leadsPage + 1; setLeadsPage(p); fetchLeadsReport(p); }}
                    disabled={leadsPage === leadsTotalPages}
                    className="gap-1"
                  >
                    Siguiente<ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* CLIENTS REPORT */}
      {reportType === 'clients' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Clientes</p>
                    <p className="text-3xl font-bold">{stats.totalClients}</p>
                  </div>
                  <Users className="h-8 w-8 text-[#1a365d]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Activos</p>
                    <p className="text-3xl font-bold text-green-600">{stats.activeClients}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Detalle de Clientes</CardTitle>
              <CardDescription>Listado completo de clientes registrados</CardDescription>
            </CardHeader>
            <CardContent>
              {clientsData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No hay datos</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead>Agente</TableHead>
                        <TableHead>Pólizas</TableHead>
                        <TableHead className="text-right">Prima Total</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Fecha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientsData.map(client => (
                        <TableRow key={client.id}>
                          <TableCell className="font-medium">{client.name || '-'}</TableCell>
                          <TableCell>{client.email || '-'}</TableCell>
                          <TableCell>{client.phone || '-'}</TableCell>
                          <TableCell>{client.agent_name || '-'}</TableCell>
                          <TableCell>{client.policies_count ?? '-'}</TableCell>
                          <TableCell className="text-right">{client.total_premium ? formatCurrency(client.total_premium) : '-'}</TableCell>
                          <TableCell>
                            <Badge className={client.status === 'activo' ? 'bg-green-500' : 'bg-gray-500'}>
                              {client.status || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{client.created_at ? formatDate(client.created_at) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {clientsTotalPages > 1 && (
                <div className="flex items-center justify-center gap-4 py-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { const p = clientsPage - 1; setClientsPage(p); fetchClientsReport(p); }}
                    disabled={clientsPage === 1}
                    className="gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    Página {clientsPage} de {clientsTotalPages} &nbsp;·&nbsp; Total: {clientsTotalRecords} clientes
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { const p = clientsPage + 1; setClientsPage(p); fetchClientsReport(p); }}
                    disabled={clientsPage === clientsTotalPages}
                    className="gap-1"
                  >
                    Siguiente<ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* AGENTS REPORT */}
      {reportType === 'agents' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Agentes</p>
                    <p className="text-3xl font-bold">{stats.totalAgents}</p>
                  </div>
                  <Users className="h-8 w-8 text-[#1a365d]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Leads</p>
                    <p className="text-3xl font-bold">{stats.totalLeads}</p>
                  </div>
                  <Target className="h-8 w-8 text-[#2d8659]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Clientes</p>
                    <p className="text-3xl font-bold">{stats.totalClientes}</p>
                  </div>
                  <Users className="h-8 w-8 text-[#2d8659]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Conversión Promedio</p>
                    <p className="text-3xl font-bold">{(stats.avgConversion ?? 0).toFixed(1)}%</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Rendimiento por Agente</CardTitle>
              <CardDescription>Métricas de desempeño del equipo de ventas</CardDescription>
            </CardHeader>
            <CardContent>
              {agentPerformance.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No hay datos de agentes disponibles
                </div>
              ) : (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%" key={`chart-${reportType}-${chartKey}-5`}>
                    <BarChart
                      layout="vertical"
                      data={agentPerformance
                        .filter(a => a.total_clients > 0 || a.total_leads > 0)
                        .sort((a,b) => b.total_clients - a.total_clients)
                        .slice(0, 15)
                        .map(a => ({
                          name: a.agent?.full_name?.split(' ')[0] || 'Agente',
                          clientes: a.total_clients,
                          leads: a.total_leads,
                        }))}
                      margin={{ left: 80, right: 20, top: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={75} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="clientes" fill="#2d8659" name="Clientes" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="leads" fill="#1a365d" name="Leads" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Detalle de Agentes</CardTitle>
              <CardDescription>Listado completo de agentes registrados</CardDescription>
            </CardHeader>
            <CardContent>
              {agentsData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No hay datos</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead className="text-right">Leads</TableHead>
                        <TableHead className="text-right">Clientes</TableHead>
                        <TableHead className="text-right">Pólizas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...agentsData].sort((a, b) =>
                        (a.user?.full_name || a.full_name || '').localeCompare(
                          b.user?.full_name || b.full_name || '', 'es')
                      ).map(agent => {
                        const perf = agentPerformance.find(a => a.agent?.id === agent.user_id || a.agent?.id === agent.id);
                        return (
                          <TableRow key={agent.id}>
                            <TableCell className="font-medium">{agent.user?.full_name || agent.full_name || '-'}</TableCell>
                            <TableCell className="text-right">{perf?.total_leads ?? agent.total_leads ?? '-'}</TableCell>
                            <TableCell className="text-right">{perf?.total_clients ?? agent.total_sales ?? '-'}</TableCell>
                            <TableCell className="text-right">{perf?.total_policies ?? agent.total_policies ?? '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* POLICIES REPORT */}
      {reportType === 'policies' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Pólizas</p>
                    <p className="text-3xl font-bold">{stats.totalPolicies}</p>
                  </div>
                  <Shield className="h-8 w-8 text-[#1a365d]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Activas</p>
                    <p className="text-3xl font-bold text-green-600">{stats.activePolicies}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Prima Total</p>
                    <p className="text-2xl font-bold">{formatCurrency(stats.totalPremium)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-[#2d8659]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Vencidas</p>
                    <p className="text-3xl font-bold text-red-600">{stats.expiredPolicies}</p>
                  </div>
                  <Receipt className="h-8 w-8 text-red-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Pólizas por Estado</CardTitle>
                <CardDescription>Distribución según estado actual</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%" key={`chart-${reportType}-${chartKey}-2`}>
                    <PieChart>
                      <Pie
                        data={(() => {
                          const statusCount = policiesData.reduce((acc, p) => {
                            const status = p.status === 'active' ? 'Activa' : p.status === 'pending' ? 'Pendiente' : p.status === 'expired' ? 'Vencida' : 'Otra';
                            acc[status] = (acc[status] || 0) + 1;
                            return acc;
                          }, {});
                          return Object.entries(statusCount).map(([name, value]) => ({ name, value }));
                        })()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {[1,2,3,4].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pólizas por Línea de Negocio</CardTitle>
                <CardDescription>Distribución por tipo de producto</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%" key={`chart-${reportType}-${chartKey}-3`}>
                    <BarChart data={(() => {
                      const lineCount = policiesData.reduce((acc, p) => {
                        const line = p.business_line || 'Otro';
                        acc[line] = (acc[line] || 0) + 1;
                        return acc;
                      }, {});
                      return Object.entries(lineCount).map(([name, count]) => ({ name, count }));
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1a365d" name="Cantidad" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Distribución por tipo de producto */}
            {polizasDistrib.length > 0 && (() => {
              const totalDist = polizasDistrib.reduce((s, d) => s + d.count, 0);
              return (
                <Card>
                  <CardHeader>
                    <CardTitle>Distribución por tipo de producto</CardTitle>
                    <CardDescription>Haz clic en un sector para filtrar la tabla</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="h-[220px] w-[220px] flex-shrink-0">
                        <ResponsiveContainer width="100%" height="100%" key={`chart-${reportType}-${chartKey}-4`}>
                          <PieChart>
                            <Pie
                              data={polizasDistrib}
                              dataKey="count"
                              nameKey="producto"
                              cx="50%"
                              cy="50%"
                              outerRadius={90}
                              onClick={(e) => setProductoFiltro(e.producto === productoFiltro ? '' : e.producto)}
                              style={{ cursor: 'pointer' }}
                            >
                              {polizasDistrib.map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]}
                                  opacity={productoFiltro && productoFiltro !== polizasDistrib[i]?.producto ? 0.4 : 1} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v, n) => [`${v} (${totalDist > 0 ? ((v / totalDist) * 100).toFixed(0) : 0}%)`, n]} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {polizasDistrib.map((d, i) => (
                          <button
                            key={d.producto}
                            onClick={() => setProductoFiltro(d.producto === productoFiltro ? '' : d.producto)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full border transition-colors ${
                              productoFiltro === d.producto
                                ? 'border-[#1a365d] bg-[#1a365d]/10 font-semibold'
                                : 'border-muted hover:border-muted-foreground'
                            }`}
                          >
                            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            {d.producto} ({d.count})
                          </button>
                        ))}
                        {productoFiltro && (
                          <button onClick={() => setProductoFiltro('')}
                            className="px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-300 text-xs">
                            ✕ Quitar filtro
                          </button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Detalle de Pólizas</CardTitle>
              <CardDescription>Listado completo de pólizas registradas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Select value={policiesDateFilter} onValueChange={v => { setPoliciesDateFilter(v); if (v !== 'start_date') setPoliciesStartDate(''); }}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filtrar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_month">Este mes</SelectItem>
                    <SelectItem value="created_year">Este año</SelectItem>
                    <SelectItem value="start_date">Por fecha inicio</SelectItem>
                  </SelectContent>
                </Select>
                {policiesDateFilter === 'start_date' && (
                  <input
                    type="date"
                    value={policiesStartDate}
                    onChange={e => setPoliciesStartDate(e.target.value)}
                    className="bg-[#1a2332] border border-[#2e3f55] text-white rounded-md px-3 py-2 text-sm [color-scheme:dark] cursor-pointer"
                  />
                )}
                <span className="text-xs text-muted-foreground">
                  {policiesDateFilter === 'created_month' && 'Pólizas registradas este mes'}
                  {policiesDateFilter === 'created_year' && 'Pólizas registradas este año'}
                  {policiesDateFilter === 'start_date' && policiesStartDate && `Inicio desde ${policiesStartDate}`}
                </span>
              </div>
              {policiesData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No hay pólizas registradas
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Número</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Línea</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Vigencia</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {policiesData.map(policy => (
                        <TableRow key={policy.id}>
                          <TableCell className="font-medium">{policy.policy_number}</TableCell>
                          <TableCell>{policy.client_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{policy.business_line}</Badge>
                          </TableCell>
                          <TableCell>{policy.product_type}</TableCell>
                          <TableCell className="text-sm">
                            {policy.start_date} - {policy.end_date}
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusLabel(policy.status).color}>
                              {getStatusLabel(policy.status).label}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {policiesTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPoliciesPage(p => Math.max(1, p - 1))}
                    disabled={policiesPage === 1}
                  >
                    ← Anterior
                  </Button>
                  <span>Página {policiesPage} de {policiesTotalPages} · Total: {policiesTotalRecords} pólizas</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPoliciesPage(p => Math.min(policiesTotalPages, p + 1))}
                    disabled={policiesPage === policiesTotalPages}
                  >
                    Siguiente →
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* INMUEBLES REPORT */}
      {reportType === 'inmuebles' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Inmuebles</p>
                    <p className="text-3xl font-bold">{stats.totalInmuebles}</p>
                  </div>
                  <Building className="h-8 w-8 text-[#1a365d]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">En Venta</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.enVenta}</p>
                  </div>
                  <Target className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Vendidos</p>
                    <p className="text-3xl font-bold text-green-600">{stats.vendidos}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Valor Total</p>
                    <p className="text-2xl font-bold">{formatCurrency(stats.valorTotal)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-[#2d8659]" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Inmuebles por Tipo</CardTitle>
                <CardDescription>Distribución según tipo de propiedad</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={(() => {
                          const typeCount = inmueblesData.reduce((acc, i) => {
                            const type = i.type || 'Otro';
                            acc[type] = (acc[type] || 0) + 1;
                            return acc;
                          }, {});
                          return Object.entries(typeCount).map(([name, value]) => ({ name, value }));
                        })()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {[1,2,3,4,5].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Inmuebles por Estado</CardTitle>
                <CardDescription>Disponibilidad actual</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(() => {
                      const statusCount = inmueblesData.reduce((acc, i) => {
                        const status = i.status || 'Otro';
                        acc[status] = (acc[status] || 0) + 1;
                        return acc;
                      }, {});
                      return Object.entries(statusCount).map(([name, count]) => ({ name, count }));
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2d8659" name="Cantidad" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Detalle de Inmuebles</CardTitle>
              <CardDescription>Listado completo de propiedades</CardDescription>
            </CardHeader>
            <CardContent>
              {inmueblesData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No hay inmuebles registrados
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Dirección</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Precio</TableHead>
                        <TableHead className="text-right">Área (m²)</TableHead>
                        <TableHead>Fecha Registro</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inmueblesData.map(inmueble => (
                        <TableRow key={inmueble.id}>
                          <TableCell className="font-medium">{inmueble.address}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{inmueble.type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              inmueble.status === 'En venta' ? 'bg-blue-500' :
                              inmueble.status === 'Vendido' ? 'bg-green-500' :
                              inmueble.status === 'En renta' ? 'bg-yellow-500' : 'bg-gray-500'
                            }>
                              {inmueble.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(inmueble.price)}</TableCell>
                          <TableCell className="text-right">{inmueble.area}</TableCell>
                          <TableCell>{inmueble.created_at}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
